<template>
  <div class="flex flex-col w-full overflow-y-auto h-full pb-6">
    <div v-if="loading" class="flex flex-col w-full justify-center items-center h-[60vh]">
      <loader />
    </div>

    <template v-else-if="messages.length">
      <div class="flex items-center gap-2 mb-3 flex-wrap">
        <button
          v-for="option in channelFilters"
          :key="option.value"
          type="button"
          :class="['filter-chip', { active: channelFilter === option.value }]"
          @click="channelFilter = option.value"
        >
          <i v-if="option.icon" :class="['pi', option.icon, 'text-xs']"></i>
          {{ option.label }}
        </button>
      </div>

      <div v-for="group in groupedMessages" :key="group.day" class="flex flex-col gap-2 mb-4">
        <div class="text-xs font-medium text-surface-500 py-1">
          {{ group.label }}
        </div>
        <div
          v-for="message in group.messages"
          :key="message.id"
          class="message-card"
          :class="{ failed: message.status === CustomerMessageStatusEnum.Failed }"
        >
          <div class="flex items-center gap-2 mb-1.5">
            <span :class="['channel-badge', message.channel]">
              <i :class="['pi', channelMeta[message.channel].icon, 'text-[11px]']"></i>
              {{ channelMeta[message.channel].label }}
            </span>
            <span v-if="templateLabel(message)" class="text-xs text-surface-500 truncate">
              {{ templateLabel(message) }}
            </span>
            <span class="text-xs text-surface-400 ms-auto flex-none">{{ formatTime(message.sentAt) }}</span>
          </div>
          <div class="text-[13px] leading-relaxed whitespace-pre-line break-words text-surface-800">
            {{ message.content || '—' }}
          </div>
          <a
            v-if="message.buttonUrl"
            :href="message.buttonUrl"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center gap-1 text-xs text-primary mt-2"
          >
            <i class="pi pi-external-link text-[10px]"></i>
            קישור בהודעה
          </a>
          <div v-if="message.status === CustomerMessageStatusEnum.Failed" class="flex items-center gap-1 text-xs text-red-600 mt-2">
            <i class="pi pi-exclamation-circle text-[11px]"></i>
            ההודעה לא נמסרה{{ message.failureReason ? ` (${message.failureReason})` : '' }}
          </div>
        </div>
      </div>

      <div v-if="!filteredMessages.length" class="text-sm text-surface-500 text-center py-6">
        אין הודעות בערוץ הזה
      </div>

      <Button
        v-if="nextCursor"
        label="הצגת הודעות קודמות"
        text
        size="small"
        class="self-center"
        :loading="loadingMore"
        @click="loadMore"
      />
    </template>

    <div v-else class="w-full flex flex-col justify-start items-center gap-2 pt-8 text-center px-6">
      <i class="pi pi-comments text-4xl text-surface-300"></i>
      <span class="text-sm text-surface-500">עוד לא נשלחו הודעות ללקוח</span>
      <span class="text-xs text-surface-400">כל הודעת SMS ו-WhatsApp שנשלחת ללקוח תופיע כאן</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { DateTime } from 'luxon';
import { useToast } from 'vue-toastification';
import {
  CustomerMessageChannelEnum,
  CustomerMessageStatusEnum,
  type ICustomer,
  type ICustomerMessage,
} from '@torim-app/types';
import { CustomersApi } from '@app/api/admin/customers-api';

const props = defineProps<{
  customer: ICustomer;
}>();

const toast = useToast();

const loading = ref(true);
const loadingMore = ref(false);
const messages = ref<ICustomerMessage[]>([]);
const nextCursor = ref<string>();
const channelFilter = ref<CustomerMessageChannelEnum | 'all'>('all');

const channelMeta: Record<CustomerMessageChannelEnum, { label: string; icon: string }> = {
  [CustomerMessageChannelEnum.Whatsapp]: { label: 'WhatsApp', icon: 'pi-whatsapp' },
  [CustomerMessageChannelEnum.Sms]: { label: 'SMS', icon: 'pi-comment' },
};

const channelFilters: Array<{ value: CustomerMessageChannelEnum | 'all'; label: string; icon?: string }> = [
  { value: 'all', label: 'הכל' },
  { value: CustomerMessageChannelEnum.Whatsapp, label: 'WhatsApp', icon: 'pi-whatsapp' },
  { value: CustomerMessageChannelEnum.Sms, label: 'SMS', icon: 'pi-comment' },
];

/** What a WhatsApp template message was about — the content alone doesn't always say. */
const templateLabels: Record<string, string> = {
  timeit_appointment_scheduled: 'אישור תור',
  timeit_appointment_reminder: 'תזכורת לתור',
  timeit_appointment_reminder_same_day: 'תזכורת ליום התור',
  timeit_appointment_cancelled: 'ביטול תור',
  timeit_appointment_rescheduled: 'שינוי מועד',
  timeit_appointment_scheduling_stopped: 'עצירת זימון',
  timeit_found_relevant_appointment: 'רשימת המתנה',
  timeit_smart_nudge: 'מילוי יומן חכם',
  timeit_appointment_review_request: 'בקשת משוב',
  timeit_invoice_receipt_v2: 'חשבונית',
  timeit_product_order_ready: 'הזמנה מוכנה',
  timeit_payment_request: 'בקשת תשלום',
  timeit_bit_payment_request: 'בקשת תשלום ב-Bit',
  timeit_card_collection_request: 'בקשה לפרטי אשראי',
  timeit_skin_scan_reminder: 'תזכורת סריקת פנים',
  timeit_new_customer_welcome: 'ברוכים הבאים',
  timeit_inactive_customer: 'לקוח לא פעיל',
  timeit_post_visit_follow_up: 'הודעה אחרי תור',
  timeit_birthday_greeting: 'ברכת יום הולדת',
  timeit_no_show_follow_up: 'אי הגעה לתור',
  timeit_voucher_received: 'שובר מתנה',
  timeit_loyalty_invite: 'הזמנה למועדון',
  timeit_loyalty_welcome: 'הצטרפות למועדון',
  timeit_consent_form_request: 'טופס הסכמה',
  timeit_appointment_delay: 'עדכון עיכוב',
};

const templateLabel = (message: ICustomerMessage) => {
  if (message.templateName) return templateLabels[message.templateName] ?? '';
  // Free-text WhatsApp messages are replies in an open chat — the AI bot's, in practice.
  return message.channel === CustomerMessageChannelEnum.Whatsapp ? 'הודעה בצ׳אט' : '';
};

const filteredMessages = computed(() =>
  channelFilter.value === 'all'
    ? messages.value
    : messages.value.filter(m => m.channel === channelFilter.value),
);

const toLocal = (date: Date | string) =>
  DateTime.fromJSDate(new Date(date)).setZone('Asia/Jerusalem').setLocale('he');

const groupedMessages = computed(() => {
  const groups: Array<{ day: string; label: string; messages: ICustomerMessage[] }> = [];
  const today = DateTime.now().setZone('Asia/Jerusalem').startOf('day');
  for (const message of filteredMessages.value) {
    const local = toLocal(message.sentAt);
    const day = local.toISODate() ?? '';
    let group = groups[groups.length - 1];
    if (!group || group.day !== day) {
      const diff = Math.round(today.diff(local.startOf('day'), 'days').days);
      const label = diff === 0 ? 'היום' : diff === 1 ? 'אתמול' : local.toFormat(local.year === today.year ? 'cccc, d בMMMM' : 'd בMMMM yyyy');
      group = { day, label, messages: [] };
      groups.push(group);
    }
    group.messages.push(message);
  }
  return groups;
});

const formatTime = (date: Date | string) => toLocal(date).toFormat('HH:mm');

const load = async () => {
  loading.value = true;
  try {
    const page = await CustomersApi.fetchCustomerMessages(props.customer.id);
    messages.value = page.messages;
    nextCursor.value = page.nextCursor;
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לטעון את ההודעות');
  } finally {
    loading.value = false;
  }
};

const loadMore = async () => {
  if (!nextCursor.value) return;
  loadingMore.value = true;
  try {
    const page = await CustomersApi.fetchCustomerMessages(props.customer.id, nextCursor.value);
    messages.value = [...messages.value, ...page.messages];
    nextCursor.value = page.nextCursor;
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לטעון הודעות נוספות');
  } finally {
    loadingMore.value = false;
  }
};

onMounted(load);
</script>

<style scoped lang="scss">
.message-card {
  @apply bg-white border border-surface-200 rounded-xl p-3 shadow-sm;

  &.failed {
    @apply border-red-200;
  }
}

.channel-badge {
  @apply inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full flex-none;

  &.whatsapp {
    background: #dcfce7;
    color: #15803d;
  }

  &.sms {
    background: #ede9fe;
    color: #6d28d9;
  }
}

.filter-chip {
  @apply inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-surface-200 bg-white text-surface-600;

  &.active {
    @apply border-primary bg-primary-50 text-primary;
  }
}
</style>
